import { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import { api } from '@/lib/axios'
import { useAuthStore } from '@/lib/auth.store'
import { absenceTypesApi } from '../api/absence.api'
import type { AbsenceType, AbsenceTypePayrollFlags } from '../types/absence.types'
import { pageWide } from '@/lib/form-styles'
import { bestaetige } from '@/lib/dialoge'
import { ZeilenBlock, ZeilenListe, Feld as Spalte, zelle, type Spalte as SpaltenDef } from '@/components/zeilenblock'
import {
  FeldStatusZeichen,
  mitStand,
  useFeld,
  useGruppe,
  useSpeicherstelle,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern'

const DEFAULT_COLORS = ['#22c55e', '#ef4444', '#3b82f6', '#f59e0b', '#8b5cf6', '#14b8a6', '#ec4899', '#64748b']

/**
 * Die drei Kennzeichen, die der Rechenkern der Lohnverrechnung je Fehlzeit-Art
 * braucht (AP-22). Beschriftung und Erklärung stehen hier einmal — Kopf und
 * Zeilen lesen beide daraus.
 */
const PAYROLL_FLAGS = [
  {
    key: 'paid',
    label: 'Bezahlt',
    short: 'Bez.',
    hint: 'Entgeltfortzahlung — die Art wird bezahlt (Urlaub, Krankenstand, Feiertag).',
  },
  {
    key: 'countsAsTarget',
    label: 'Zählt als Soll',
    short: 'Soll',
    hint: 'Ausfallsprinzip: Das Tages-Soll gilt als erfüllt — sonst entsteht eine Fehlstunde in Höhe des Solls.',
  },
  {
    key: 'timeCompensation',
    label: 'Zeitausgleich',
    short: 'ZA',
    hint: 'Verbraucht zusätzlich Guthaben vom ZA-Konto. Wirkt zusammen mit „Zählt als Soll".',
  },
] as const satisfies readonly { key: keyof AbsenceTypePayrollFlags; label: string; short: string; hint: string }[]

/**
 * Freigabe für den KI-Assistenten (AP-18, Entscheidung A-3): Die Bezeichnung der Art darf an die
 * KI. Ab Werk aus. Die Spalte gibt es nur, wenn der Baustein `AI_ASSISTANT` gebucht ist — was
 * nicht gebucht ist, wird nicht angeboten (`.claude/rules/modul-sichtbarkeit.md`).
 */
const KI_FREIGABE = 'aiNameAllowed'
const BAUSTEIN_ASSISTENT = 'AI_ASSISTANT'
/** Beschriftung der Freigabe in der Sprache des Benutzers — `null`, solange der Baustein fehlt. */
interface KiFreigabe {
  label: string
  short: string
  hint: string
}

/** Die Spalten der Liste — die Freigabe-Spalte nur mit gebuchtem Assistenten. */
function spalten(kiKurz: string | null): SpaltenDef[] {
  return [
    { key: 'farbe', label: 'Farbe', spur: '4rem', min: 56, schmal: 'w-16' },
    { key: 'name', label: 'Name', spur: 'minmax(10rem,3fr)', min: 160, schmal: 'w-full' },
    ...PAYROLL_FLAGS.map((f) => ({ key: f.key, label: f.short, spur: '3.5rem', min: 48, schmal: 'w-16' })),
    ...(kiKurz ? [{ key: KI_FREIGABE, label: kiKurz, spur: '3.5rem', min: 48, schmal: 'w-16' }] : []),
    { key: 'sortOrder', label: 'Sortierung', spur: '6rem', min: 70, schmal: 'w-24', rechts: true },
    { key: 'aktion', label: '', spur: '2.5rem', min: 30, schmal: 'w-10' },
  ]
}

/**
 * **Fehlzeit-Typen** — Farbe, Name, die drei Lohn-Kennzeichen, die Freigabe für den
 * KI-Assistenten (nur mit gebuchtem Baustein) und die Reihenfolge.
 *
 * AP-36 Phase E: Zeilenblock statt Dialog. Jede Zeile ist eine Stelle
 * (`absenceType:<id>`) und speichert beim Verlassen; die leere letzte Zeile legt
 * an, sobald ein Name dasteht. Die Kennzeichen wirken heute noch nirgends — sie
 * sind die Vorleistung für AP-22 und stehen bewusst alle auf „aus".
 */
export default function AbsenceTypesPage() {
  const { t } = useTranslation()
  const { hasModule } = useAuthStore()
  // Nur mit gebuchtem Assistenten: sonst gibt es weder Spalte noch Erklärung.
  const mitKi = hasModule(BAUSTEIN_ASSISTENT)
  const ki = useMemo<KiFreigabe | null>(
    () => (mitKi ? { label: t('absenceTypes.aiName.label'), short: t('absenceTypes.aiName.short'), hint: t('absenceTypes.aiName.hint') } : null),
    [mitKi, t],
  )
  const SPALTEN = useMemo(() => spalten(ki?.short ?? null), [ki])
  const [rows, setRows] = useState<AbsenceType[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchRows = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await absenceTypesApi.list({ isActive: true, limit: 200 })
      setRows(res.data.data)
    } catch (err) {
      console.error('Failed to load absence types', err)
      setError('Fehlzeit-Typen konnten nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchRows()
  }, [fetchRows])

  const loeschen = async (typ: AbsenceType) => {
    if (!(await bestaetige(`Fehlzeit-Typ „${typ.name}" löschen? Bereits erfasste Fehlzeiten bleiben erhalten.`, { ok: 'Löschen', gefaehrlich: true }))) return
    try {
      await absenceTypesApi.delete(typ.id)
      void fetchRows()
    } catch (err) {
      console.error('Failed to delete absence type', err)
      setError('Fehlzeit-Typ konnte nicht gelöscht werden.')
    }
  }

  return (
    <div className={pageWide}>
      <div>
        <h1 className="text-3xl font-bold text-foreground">Fehlzeit-Typen</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Definiere die Fehlzeit-Typen (z.B. Urlaub, Krankheit, Zeitausgleich, Behördenweg). Eine Zeile wird
          gespeichert, sobald du sie verlässt.
        </p>
      </div>

      {error && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

      <ZeilenListe spalten={SPALTEN}>
        {loading && rows.length === 0 && <p className="px-4 py-6 text-center text-sm text-muted-foreground">Laden…</p>}
        {rows.map((typ) => (
          <TypZeile key={typ.id} typ={typ} ki={ki} onLoeschen={() => void loeschen(typ)} />
        ))}
        <NeuerTyp anzahl={rows.length} ki={ki} onAngelegt={() => void fetchRows()} onFehler={setError} />
      </ZeilenListe>

      <ul className="space-y-1 text-xs text-muted-foreground">
        {PAYROLL_FLAGS.map((f) => (
          <li key={f.key}>
            <span className="font-medium text-foreground">{f.short}</span> — {f.label}: {f.hint}
          </li>
        ))}
        {ki && (
          <li>
            <span className="font-medium text-foreground">{ki.short}</span> — {ki.label}: {ki.hint}
          </li>
        )}
      </ul>
    </div>
  )
}

function TypZeile({ typ, ki, onLoeschen }: { typ: AbsenceType; ki: KiFreigabe | null; onLoeschen: () => void }) {
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `absenceType:${typ.id}`,
    bezeichnung: typ.name,
    daten: typ as unknown as Datensatz,
    schreibe: (patch, stand) => api.patch<Datensatz>(`/absence-types/${typ.id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/absence-types/${typ.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: typ.name })
  const farbe = useFeld(stelle, 'color', { gruppe, art: 'auswahl', label: 'Farbe' })
  const name = useFeld(stelle, 'name', { gruppe, pflicht: true, label: 'Name' })
  const reihenfolge = useFeld<number | null>(stelle, 'sortOrder', { gruppe, art: 'zahl', pflicht: true, label: 'Sortierung' })

  return (
    <ZeilenBlock gruppe={gruppe} schluessel={typ.id}>
      <Spalte spalte="farbe">
        <span className="flex items-center gap-1">
          <FarbFeld wert={farbe.wert ?? DEFAULT_COLORS[0]} onWahl={(hex) => farbe.setze(hex)} />
          <FeldStatusZeichen status={farbe} />
        </span>
      </Spalte>
      <Spalte spalte="name">
        <input aria-label="Name" className={zelle} data-start {...name.props} />
      </Spalte>
      {PAYROLL_FLAGS.map((f) => (
        <Spalte key={f.key} spalte={f.key}>
          <Kennzeichen stelle={stelle} gruppe={gruppe} feld={f.key} label={f.label} hinweis={f.hint} />
        </Spalte>
      ))}
      {ki && (
        <Spalte spalte={KI_FREIGABE}>
          <Kennzeichen stelle={stelle} gruppe={gruppe} feld={KI_FREIGABE} label={ki.label} hinweis={ki.hint} />
        </Spalte>
      )}
      <Spalte spalte="sortOrder">
        <input aria-label="Sortierung" inputMode="numeric" className={`${zelle} text-right`} {...reihenfolge.props} />
      </Spalte>
      <Spalte spalte="aktion">
        <button
          type="button"
          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          onClick={onLoeschen}
          title="Löschen"
          aria-label="Löschen"
        >
          <Trash2 size={14} />
        </button>
      </Spalte>
    </ZeilenBlock>
  )
}

function Kennzeichen({
  stelle,
  gruppe,
  feld,
  label,
  hinweis,
}: {
  stelle: Speicherstelle<unknown>
  gruppe: ReturnType<typeof useGruppe>
  feld: string
  label: string
  hinweis: string
}) {
  const f = useFeld<boolean>(stelle, feld, { gruppe, art: 'schalter', label })
  return (
    <span className="flex items-center justify-center gap-1" title={hinweis}>
      <input type="checkbox" aria-label={label} className="h-4 w-4 accent-[hsl(var(--primary))]" {...f.schalter} />
      <FeldStatusZeichen status={f} />
    </span>
  )
}

/** Farbwähler, der erst beim Schließen übernimmt (der native `change`, nicht jede Bewegung). */
function FarbFeld({ wert, onWahl }: { wert: string; onWahl: (hex: string) => void }) {
  const [vorschau, setVorschau] = useState(wert)
  useEffect(() => setVorschau(wert), [wert])
  return (
    <input
      type="color"
      aria-label="Farbe"
      className="h-6 w-9 cursor-pointer rounded border border-input bg-background"
      value={vorschau}
      onChange={(e) => setVorschau(e.target.value)}
      onBlur={(e) => e.target.value !== wert && onWahl(e.target.value)}
    />
  )
}

/** Die leere letzte Zeile: legt beim Verlassen an, sobald ein Name dasteht. */
function NeuerTyp({ anzahl, ki, onAngelegt, onFehler }: { anzahl: number; ki: KiFreigabe | null; onAngelegt: () => void; onFehler: (t: string | null) => void }) {
  const leer = { name: '', color: DEFAULT_COLORS[0], paid: false, countsAsTarget: false, timeCompensation: false, aiNameAllowed: false }
  const [neu, setNeu] = useState(leer)

  const anlegen = async () => {
    if (!neu.name.trim()) return
    try {
      onFehler(null)
      // Die Freigabe geht nur mit, wenn es die Spalte gibt — ein nicht angebotener Schalter wird nicht gesendet.
      const { aiNameAllowed, ...rest } = neu
      await absenceTypesApi.create({ ...rest, ...(ki ? { aiNameAllowed } : {}), name: neu.name.trim(), sortOrder: anzahl })
      setNeu(leer)
      onAngelegt()
    } catch (err) {
      console.error('Failed to create absence type', err)
      onFehler('Fehlzeit-Typ konnte nicht angelegt werden.')
    }
  }

  return (
    <ZeilenBlock neu onVerlassen={() => void anlegen()} onVerwerfen={() => setNeu(leer)}>
      <Spalte spalte="farbe" leer>
        <FarbFeld wert={neu.color} onWahl={(hex) => setNeu({ ...neu, color: hex })} />
      </Spalte>
      <Spalte spalte="name">
        <input
          aria-label="Neuer Fehlzeit-Typ"
          className={zelle}
          placeholder="Neuer Fehlzeit-Typ …"
          value={neu.name}
          onChange={(e) => setNeu({ ...neu, name: e.target.value })}
        />
      </Spalte>
      {PAYROLL_FLAGS.map((f) => (
        <Spalte key={f.key} spalte={f.key} leer>
          <span className="flex items-center justify-center" title={f.hint}>
            <input
              type="checkbox"
              aria-label={f.label}
              className="h-4 w-4 accent-[hsl(var(--primary))]"
              checked={neu[f.key]}
              onChange={(e) => setNeu({ ...neu, [f.key]: e.target.checked })}
            />
          </span>
        </Spalte>
      ))}
      {ki && (
        <Spalte spalte={KI_FREIGABE} leer>
          <span className="flex items-center justify-center" title={ki.hint}>
            <input
              type="checkbox"
              aria-label={ki.label}
              className="h-4 w-4 accent-[hsl(var(--primary))]"
              checked={neu.aiNameAllowed}
              onChange={(e) => setNeu({ ...neu, aiNameAllowed: e.target.checked })}
            />
          </span>
        </Spalte>
      )}
      <Spalte spalte="sortOrder" leer>
        <span />
      </Spalte>
      <Spalte spalte="aktion" leer>
        <span />
      </Spalte>
    </ZeilenBlock>
  )
}
